import { useNavigate } from "react-router-dom";
import { ProgressBar } from "@/components/ProgressBar";
import { SectionHead } from "@/components/SectionHead";
import { STATUS_DOT, STATUS_LABEL } from "@/components/status";
import { useActivity } from "@/hooks/useActivity";
import { useIssueViews } from "@/hooks/useIssueViews";
import { useAllMastery } from "@/hooks/useMastery";
import { useSettings } from "@/hooks/useSettings";
import { pad, todayShort } from "@/lib/format";

export function Progress() {
  const navigate = useNavigate();
  const issues = useIssueViews();
  const mastery = useAllMastery();
  const activity = useActivity();
  const { settings } = useSettings();

  const totalAll = issues.reduce((acc, i) => acc + i.total, 0);
  const totalDue = issues.reduce((acc, i) => acc + i.due, 0);

  return (
    <div className="page">
      <SectionHead
        kicker="Progress · the ledger"
        title={
          <>
            The <span className="italic">ledger</span>.
          </>
        }
        right={
          <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Updated · {todayShort()}
          </div>
        }
      />

      <section className="container">
        <div className="stats-grid">
          {/* Four cells exactly — .stat-cell's border rules are cut for a 4-up grid.
              Days completed lives in the ledger's status column below, and words
              touched in the mastery bar, so this strip is today's study loop. */}
          <StatCell
            label="New today"
            value={`${activity.today.learned}`}
            unit={` / ${settings.dailyGoal}`}
            bar={<ProgressBar value={activity.today.learned} max={settings.dailyGoal} />}
          />
          <StatCell
            label="Due now"
            value={`${totalDue}`}
            unit=" cards"
            bar={<ProgressBar value={totalDue} max={Math.max(totalDue, mastery.totals.touched, 1)} />}
          />
          <StatCell
            label="Words mastered"
            value={`${mastery.totals.known}`}
            unit={` / ${totalAll}`}
            bar={<ProgressBar value={mastery.totals.known} max={totalAll} />}
          />
          <StatCell
            label="Streak"
            value={`${activity.streak}`}
            unit={activity.streak === 1 ? " day" : " days"}
            bar={<ProgressBar value={Math.min(activity.streak, 30)} max={30} />}
          />
        </div>
      </section>

      <section className="container" style={{ marginTop: "var(--s-7)" }}>
        <h2 className="t-display" style={{ fontSize: "var(--t-xl)", margin: "0 0 var(--s-4) 0" }}>
          Mastery breakdown
        </h2>
        <MasteryBar
          known={mastery.totals.known}
          learning={mastery.totals.learning}
          unknown={mastery.totals.unknown}
          total={totalAll}
        />
        <div className="row-c" style={{ gap: 24, marginTop: 12, flexWrap: "wrap" }}>
          <LegendItem dot="moss" label="Known" value={mastery.totals.known} total={totalAll} />
          <LegendItem dot="amber" label="Learning" value={mastery.totals.learning} total={totalAll} />
          <LegendItem dot="red" label="Unknown" value={mastery.totals.unknown} total={totalAll} />
          <LegendItem
            dot="empty"
            label="Untouched"
            value={totalAll - mastery.totals.touched}
            total={totalAll}
          />
        </div>
      </section>

      <section className="container" style={{ marginTop: "var(--s-7)" }}>
        <h2 className="t-display" style={{ fontSize: "var(--t-xl)", margin: "0 0 var(--s-4) 0" }}>
          Day ledger
        </h2>
        <div style={{ border: "var(--border-thin)" }}>
          <div className="ledger-head">
            <span>Nº</span>
            <span>Theme</span>
            <span>Status</span>
            <span style={{ textAlign: "right" }}>Progress</span>
            <span style={{ textAlign: "right" }}>%</span>
            <span></span>
          </div>
          {issues.map((it) => (
            <div key={it.day} className="ledger-row">
              <span className="t-mono" style={{ color: "var(--text-faint)" }}>{pad(it.day)}</span>
              <span className="t-display" style={{ fontSize: "var(--t-md)" }}>
                {it.theme_en}{" "}
                <span className="italic" style={{ color: "var(--text-muted)" }}>· {it.theme_vi}</span>
              </span>
              <span className="row-c" style={{ gap: 6 }}>
                <span className={`dot dot--${STATUS_DOT[it.status]}`} />
                <span className="t-meta" style={{ fontSize: 10 }}>{STATUS_LABEL[it.status]}</span>
              </span>
              <span className="t-mono" style={{ fontSize: 12, textAlign: "right" }}>
                {pad(it.progress)} / {it.total}
              </span>
              <span
                className="t-mono"
                style={{ fontSize: 12, textAlign: "right", color: "var(--text-muted)" }}
              >
                {Math.round((it.progress / it.total) * 100)}%
              </span>
              <span style={{ textAlign: "right" }}>
                {it.status !== "locked" ? (
                  <a
                    onClick={() => navigate(`/day/${it.day}`)}
                    className="t-mono"
                    style={{ cursor: "pointer", fontSize: 12, color: "var(--text-muted)" }}
                  >
                    open →
                  </a>
                ) : (
                  <span className="t-mono" style={{ fontSize: 12, color: "var(--text-faint)" }}>—</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

interface StatCellProps {
  label: string;
  value: string;
  unit: string;
  bar?: React.ReactNode;
}

function StatCell({ label, value, unit, bar }: StatCellProps) {
  return (
    <div className="stat-cell">
      <span className="stat-cell__label">{label}</span>
      <span className="stat-cell__value">
        {value}
        <span className="stat-cell__unit">{unit}</span>
      </span>
      {bar}
    </div>
  );
}

interface MasteryBarProps {
  known: number;
  learning: number;
  unknown: number;
  total: number;
}

function MasteryBar({ known, learning, unknown, total }: MasteryBarProps) {
  if (total === 0) {
    return (
      <div className="stack-bar">
        <div className="stack-bar__seg stack-bar__seg--rest" style={{ width: "100%" }}>
          0% untouched
        </div>
      </div>
    );
  }
  const knownPct = (known / total) * 100;
  const learnPct = (learning / total) * 100;
  const unknownPct = (unknown / total) * 100;
  const restPct = Math.max(0, 100 - knownPct - learnPct - unknownPct);
  return (
    <div className="stack-bar">
      {knownPct > 0 && (
        <div className="stack-bar__seg stack-bar__seg--known" style={{ width: `${knownPct}%` }}>
          {Math.round(knownPct)}% known
        </div>
      )}
      {learnPct > 0 && (
        <div className="stack-bar__seg stack-bar__seg--learning" style={{ width: `${learnPct}%` }}>
          {Math.round(learnPct)}% learning
        </div>
      )}
      {unknownPct > 0 && (
        <div className="stack-bar__seg stack-bar__seg--unknown" style={{ width: `${unknownPct}%` }}>
          {Math.round(unknownPct)}% unknown
        </div>
      )}
      {restPct > 0 && (
        <div className="stack-bar__seg stack-bar__seg--rest" style={{ width: `${restPct}%` }}>
          {Math.round(restPct)}% untouched
        </div>
      )}
    </div>
  );
}

function LegendItem({
  dot,
  label,
  value,
  total,
}: {
  dot: "moss" | "amber" | "red" | "empty";
  label: string;
  value: number;
  total: number;
}) {
  const pct = total === 0 ? 0 : Math.round((value / total) * 100);
  return (
    <div className="row-c" style={{ gap: 8 }}>
      <span className={`dot dot--${dot}`} />
      <span className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>{label}</span>
      <span className="t-mono" style={{ fontSize: 12 }}>
        {value} <span style={{ color: "var(--text-faint)" }}>· {pct}%</span>
      </span>
    </div>
  );
}
